import { motion } from 'motion/react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useGalleryImages, heroFallbackImage } from '@/pages/home/galleryImages';

const SLIDE_INTERVAL = 5000;

export default function HeroSection() {
  const ref = useRef<HTMLElement>(null);
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  const { images: galleryImages, loading } = useGalleryImages();

  const heroImages = galleryImages.length > 0 ? galleryImages : [heroFallbackImage];

  const goTo = useCallback(
    (index: number) => {
      setDirection(index > current ? 1 : -1);
      setCurrent(index);
    },
    [current],
  );

  const goNext = useCallback(() => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % heroImages.length);
  }, [heroImages.length]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  }, [heroImages.length]);

  useEffect(() => {
    if (isPaused || heroImages.length <= 1) return;
    const timer = setInterval(goNext, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [isPaused, goNext, heroImages.length]);

  const bgVariants = {
    enter: () => ({ opacity: 0, scale: 1.08 }),
    center: { opacity: 1, scale: 1 },
    exit: () => ({ opacity: 0, scale: 0.98 }),
  };

  const hasMultiple = heroImages.length > 1;

  return (
    <section ref={ref} id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        {hasMultiple ? (
          <>
            {heroImages.map((img, i) => (
              <motion.div
                key={img.src}
                custom={direction}
                variants={bgVariants}
                initial="enter"
                animate={i === current ? 'center' : 'exit'}
                transition={{ duration: 0.8, ease: 'easeInOut' }}
                className="absolute inset-0"
                style={{ opacity: i === current ? 1 : 0, pointerEvents: i === current ? 'auto' : 'none' }}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ))}
          </>
        ) : (
          <div className="absolute inset-0">
            <img
              src={heroImages[0].src}
              alt={heroImages[0].alt}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/60 to-foreground-950/80"></div>
      </div>

      {hasMultiple && (
        <>
          <button
            onClick={goPrev}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-background-50/15 hover:bg-background-50/30 rounded-full text-background-50 transition-all cursor-pointer backdrop-blur-sm"
            aria-label="Previous photo"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            onClick={goNext}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-background-50/15 hover:bg-background-50/30 rounded-full text-background-50 transition-all cursor-pointer backdrop-blur-sm"
            aria-label="Next photo"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </>
      )}

      {hasMultiple && (
        <div
          className="absolute bottom-24 md:bottom-28 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className={`rounded-full transition-all duration-300 cursor-pointer ${
                index === current
                  ? 'bg-background-50 w-8 h-2'
                  : 'bg-background-50/40 hover:bg-background-50/70 w-2 h-2'
              }`}
              aria-label={`Photo ${index + 1}`}
            />
          ))}
        </div>
      )}

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
        <div>
          <div className="w-24 h-24 md:w-28 md:h-28 mx-auto mb-6 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/f5735cd9a713fc9d715878d69c8293bc.png"
              alt="Ashok Telecom Logo"
              className="w-full h-full object-contain drop-shadow-lg"
            />
          </div>

          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-background-50 mb-2 leading-tight">
            Ashok Telecom
          </h1>
          <p className="font-heading text-lg md:text-2xl text-background-100 mb-4">
            &amp; Communication
          </p>

          <p className="text-base md:text-lg text-primary-300 font-medium mb-2">
            Connecting Lifestyles &amp; Households
          </p>
          <p className="text-sm md:text-base text-background-300 mb-8">
            Mobiles · Home &amp; Kitchen Appliances · DTH · Money Transfer · Services — All in One Place
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/book-service"
              className="bg-accent-500 hover:bg-accent-600 text-background-50 px-8 py-3.5 rounded-full font-semibold text-base transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-2 hover:scale-105 active:scale-95"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-check-line text-sm"></i>
              </span>
              Book a Service
            </a>
            <a
              href="tel:+919964111963"
              className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3.5 rounded-full font-semibold text-base transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-2 hover:scale-105 active:scale-95"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-sm"></i>
              </span>
              +91 99641 11963
            </a>
            <a
              href="#services"
              className="border-2 border-background-300/60 hover:border-background-50 text-background-100 hover:text-background-50 px-8 py-3.5 rounded-full font-semibold text-base transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-2 hover:scale-105 active:scale-95"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-down-line text-sm"></i>
              </span>
              View Services
            </a>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <a href="#services" className="flex flex-col items-center gap-1 text-background-300 hover:text-background-100 transition-colors cursor-pointer">
            <span className="text-xs font-medium">Scroll Down</span>
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-arrow-down-s-line text-xl animate-bounce"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}